import React, { useState } from 'react';
import { Modal } from '../common/components/ui/Modal';

const TermsContent: React.FC = () => (
  <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
    <p>Invo is a free invoice generation tool provided on an "as is" basis.</p>
    <p>
      While we aim to keep the tool accurate and available, we do not guarantee
      that the website will always be error-free, uninterrupted, or suitable for
      every use case.
    </p>
    <p>
      Users are responsible for checking the accuracy, completeness, and legality
      of any invoices they create using the tool.
    </p>
    <p>Invo does not provide legal, tax, or accounting advice.</p>
    <p>
      All content, branding, design, and templates on this website remain the
      property of Invo unless otherwise stated.
    </p>
    <p>
      You may use the tool for lawful purposes only. You must not misuse the site,
      attempt to disrupt it, or copy or redistribute its content without permission.
    </p>
    <p>
      We may update, change, or remove parts of the tool at any time without
      notice.
    </p>
    <p>By using this website, you agree to these terms.</p>
  </div>
);

const PrivacyContent: React.FC = () => (
  <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
    <p>
      Invo does not ask users to create an account or submit personal information
      to use the tool.
    </p>
    <p>
      However, like most websites, basic technical information may be processed by
      the website host or infrastructure providers — such as IP address, browser
      type, device information, and server logs — for security, performance, and
      maintenance purposes.
    </p>
    <p>We do not use this information to identify individual users.</p>
    <p>
      If analytics or additional tracking tools are introduced in future, this
      notice will be updated.
    </p>
    <p>
      For any privacy-related questions, contact:{' '}
      <a
        href="mailto:involab.official@gmail.com"
        className="text-primary hover:underline"
      >
        involab.official@gmail.com
      </a>
    </p>
  </div>
);

const AppFooter: React.FC = () => {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <footer className="w-full border-t border-border bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Top row: brand + tagline + navigation */}
        <div className="flex flex-col gap-6 sm:gap-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <a
                href="/"
                className="text-base font-bold text-foreground hover:text-primary transition-colors"
                aria-label="Invo — Free Invoice Generator"
              >
                Invo
              </a>
              <p className="text-sm text-muted-foreground">
                Free online invoice generator &amp; PDF invoice maker
              </p>
            </div>

            {/* Crawlable navigation links */}
            <nav aria-label="Footer navigation" className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <a href="/" className="hover:text-foreground transition-colors">
                Invoice Generator
              </a>
              <a href="/#how-to-create-invoice" className="hover:text-foreground transition-colors">
                How It Works
              </a>
              <a href="/#invoice-generator-faqs" className="hover:text-foreground transition-colors">
                FAQ
              </a>
              <button
                onClick={() => setTermsOpen(true)}
                className="hover:text-foreground transition-colors underline-offset-2 hover:underline"
              >
                Terms of Use
              </button>
              <button
                onClick={() => setPrivacyOpen(true)}
                className="hover:text-foreground transition-colors underline-offset-2 hover:underline"
              >
                Privacy Notice
              </button>
            </nav>
          </div>

          {/* Bottom row: copyright + contact */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 border-t border-border text-sm text-muted-foreground">
            <span>&copy; {new Date().getFullYear()} Invo. Free invoice generator for freelancers, agencies, and small businesses.</span>
            <span>
              Contact:{' '}
              <a
                href="mailto:involab.official@gmail.com"
                className="text-primary hover:underline"
              >
                involab.official@gmail.com
              </a>
            </span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        title="Terms of Use"
        size="md"
        titleId="terms-modal-title"
      >
        <TermsContent />
      </Modal>

      <Modal
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        title="Privacy Notice"
        size="md"
        titleId="privacy-modal-title"
      >
        <PrivacyContent />
      </Modal>
    </footer>
  );
};

export default AppFooter;
